import { maybeDivide, maybeMultiply } from '@calculations/utilities/maybe-math';
import { RunRateType, ScrapYieldType } from '@snap-types/models/enums';
import { SnapOperation } from '@snap-types/models/snap-operation';
import { Decimal, ZERO } from '@snap-types/types/decimal';

import {
  calculateRequirementCosts,
  SnapRequirementCostParams,
  SnapRequirementCostResults,
} from './requirement';

export type SnapOperationCostParams = Omit<
  SnapOperation,
  | 'uuid'
  | 'originalOperationUuid'
  | 'snapWorkOrderUuid'
  | 'createdAt'
  | 'updatedAt'
> & {
  snapRequirements: SnapRequirementCostParams[];
};

export type SnapOperationCalculationResults = {
  sequenceNumber: number;
  startQuantity: Decimal;
  endQuantity: Decimal;
  materialCost: Decimal;
  burdenCost: Decimal;
  laborCost: Decimal;
  serviceCost: Decimal;
  runHours: Decimal;
  requirementResults: SnapRequirementCostResults[];
  totalCost: Decimal;
};

export function calculateOperationCosts(
  params: SnapOperationCostParams,
  endQuantity: Decimal,
): SnapOperationCalculationResults {
  // first derive the start quantity of the operation from scrap values
  const startQuantity = calculateOperationStartQty(params, endQuantity);

  // get the requirement calculations which will provide material costs
  const requirementResults = params.snapRequirements.map((requirement) =>
    calculateRequirementCosts(requirement, startQuantity, endQuantity),
  );

  // compute the hours that the operation will run
  // required for run cost and burden
  const runHours = calculateRunHours(params, endQuantity);

  // setup cost and run cost should be computed separately
  // because it matters for computing burden
  const setupCost = calculateSetupCost(params);
  const runCost = calculateRunCost(params, runHours, endQuantity);

  // Sum the various requirements costs to get the total costs for each category
  // NOTE: Materials costs come ONLY from the requirements; the other costs
  //       come from both the requirements and the operation itself
  const materialCost = requirementResults.reduce(function (total, req) {
    return total.plus(req.materialCost);
  }, ZERO);

  const partLaborCost = requirementResults.reduce(function (total, req) {
    return total.plus(req.laborCost);
  }, ZERO);

  const partBurdenCost = requirementResults.reduce(function (total, req) {
    return total.plus(req.burdenCost);
  }, ZERO);

  const partServiceCost = requirementResults.reduce(function (total, req) {
    return total.plus(req.serviceCost);
  }, ZERO);

  let burdenCost = calculateBurdenCost(
    params,
    runHours,
    endQuantity,
    setupCost,
    runCost,
  );
  burdenCost = burdenCost.plus(partBurdenCost);

  // "Original" labor and service for THIS operation are both based on "run cost" and mutually exclusive
  // PART labor and service costs for required parts still need to flow through
  // this operation and be included in the final result
  let laborCost = ZERO;
  let serviceCost = ZERO;

  if (params.serviceId) {
    serviceCost = calculateServiceCost(params, setupCost, runCost, endQuantity);
    serviceCost = serviceCost.plus(partServiceCost);
    laborCost = partLaborCost;
  } else {
    serviceCost = partServiceCost;
    laborCost = setupCost.plus(runCost).plus(partLaborCost);
  }

  return {
    sequenceNumber: params.sequenceNo,
    startQuantity,
    endQuantity,
    materialCost,
    burdenCost,
    laborCost,
    serviceCost,
    runHours,
    requirementResults,
    totalCost: Decimal.sum(materialCost, burdenCost, laborCost, serviceCost),
  };
}

export function calculateOperationStartQty(
  {
    scrapYieldPct,
    scrapYieldType,
    fixedScrapUnits,
  }: Pick<
    SnapOperationCostParams,
    'scrapYieldPct' | 'scrapYieldType' | 'fixedScrapUnits'
  >,
  endQuantity: Decimal,
): Decimal {
  // get the % yield of the operation
  let percentYield = new Decimal('1');

  if (scrapYieldPct && scrapYieldType === ScrapYieldType.SCRAP) {
    percentYield = new Decimal('1').minus(scrapYieldPct.dividedBy(100));
  } else if (scrapYieldPct && scrapYieldType === ScrapYieldType.YIELD) {
    if (scrapYieldPct.lessThanOrEqualTo(0)) {
      // ScrapYieldType.YIELD percent must be greater than 0.
      // throw prevents `Infinity` result via 0 / 100
      throw new Error(
        'ScrapYieldType.YIELD: scrapYieldPct must be greater than 0',
      );
    }

    percentYield = scrapYieldPct.dividedBy(100);
  }

  // divide by the percent yield and add fixed scrap to get the input amount
  // const startQuantity = endQuantity / percentYield + scrapFixedUnits;
  const startQuantity = endQuantity
    .dividedBy(percentYield)
    .plus(fixedScrapUnits || new Decimal(0));

  return startQuantity;
}

/**
 * The visual "run_hrs" field in the DB seems to be calculated and rounded,
 * and using it straight up seems to introduce marginal errors in the calculations.
 * Thus, calculate the run hours based on the quantity and provided run rate data.
 */
export function calculateRunHours(
  {
    run,
    runType,
    loadSizeQty,
  }: Pick<SnapOperationCostParams, 'run' | 'runType' | 'loadSizeQty'>,
  endQuantity: Decimal,
): Decimal {
  if (endQuantity.eq(0) || !run || run.eq(0)) {
    return ZERO;
  }

  // If `estLoadSizeQty` is 0 or null, use 1 as the divisor for the number of
  //  loads calculation. Avoids a divide by 0 blowing this up to Infinity.
  const numberOfLoadsDivisor = loadSizeQty || new Decimal('1');
  const numberOfLoads = endQuantity.dividedBy(numberOfLoadsDivisor).ceil();

  switch (runType) {
    case RunRateType.PCS_PER_HR:
      return endQuantity.dividedBy(run);

    case RunRateType.HRS_PER_PC:
      return run.times(endQuantity);

    case RunRateType.DAYS_PER_PC:
      return run.times(24).times(endQuantity);

    case RunRateType.HRS_PER_LOAD:
      return run.times(numberOfLoads);

    case RunRateType.DAYS_PER_LOAD:
      return run.times(24).times(numberOfLoads);

    case RunRateType.PCS_PER_MIN:
      return endQuantity.dividedBy(run).dividedBy(60);

    case RunRateType.MIN_PER_PC:
      return run.times(endQuantity).dividedBy(60);

    case RunRateType.PCS_PER_DAY:
      return endQuantity.dividedBy(run).times(24);

    case RunRateType.LOADS_PER_HR:
      return numberOfLoads.dividedBy(run);

    case RunRateType.LOADS_PER_DAY:
      return numberOfLoads.dividedBy(run).times(24);

    /* v8 ignore next 4 */
    default: {
      const exhaustive: never = runType;
      throw new Error(`Unhandled run rate type: ${exhaustive}`);
    }
  }
}

export function calculateBurdenCost(
  {
    burPerHrSetup,
    burPerHrRun,
    burPerUnitRun,
    setupHrs,
    burPercentSetup,
    burPercentRun,
  }: Pick<
    SnapOperationCostParams,
    | 'burPerHrSetup'
    | 'burPerHrRun'
    | 'burPerUnitRun'
    | 'setupHrs'
    | 'burPercentRun'
    | 'burPercentSetup'
  >,
  runHours: Decimal,
  endQuantity: Decimal,
  setupCost: Decimal,
  runCost: Decimal,
): Decimal {
  const burdenCost = Decimal.sum(
    maybeMultiply(burPerHrSetup, setupHrs),
    maybeMultiply(burPerHrRun, runHours),
    maybeMultiply(endQuantity, burPerUnitRun),
    maybeDivide(burPercentSetup, 100).times(setupCost),
    maybeDivide(burPercentRun, 100).times(runCost),
  );

  return burdenCost;
}

export function calculateSetupCost({
  setupCostPerHr,
  setupHrs,
}: Pick<SnapOperationCostParams, 'setupCostPerHr' | 'setupHrs'>) {
  return maybeMultiply(setupCostPerHr, setupHrs);
}

export function calculateRunCost(
  {
    runCostPerHr,
    runCostPerUnit,
  }: Pick<SnapOperationCostParams, 'runCostPerHr' | 'runCostPerUnit'>,
  runHours: Decimal,
  endQuantity: Decimal,
): Decimal {
  return Decimal.sum(
    maybeMultiply(runCostPerHr, runHours),
    maybeMultiply(endQuantity, runCostPerUnit),
  );
}

export function calculateServiceCost(
  {
    serviceBaseCharge,
    serviceMinCharge,
    serviceChargePerUnit,
  }: Pick<
    SnapOperationCostParams,
    'serviceBaseCharge' | 'serviceMinCharge' | 'serviceChargePerUnit'
  >,
  setupCost: Decimal,
  runCost: Decimal,
  endQuantity: Decimal,
): Decimal {
  const pricePerUnit = maybeMultiply(serviceChargePerUnit, endQuantity);

  const calculatedServiceCharge = setupCost
    .plus(runCost)
    .plus(pricePerUnit)
    .plus(serviceBaseCharge ?? 0);

  const minCharge = serviceMinCharge ?? ZERO;

  if (calculatedServiceCharge.lt(minCharge)) {
    return minCharge;
  }

  return calculatedServiceCharge;
}
